"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MockSerialTransport = void 0;
const errors_1 = require("./errors");
const DEFAULT_TELEGRAM_END_TIMEOUT_MS = 20;
class MockSerialTransport {
    isOpen = false;
    config = {
        baudRate: 9600,
        dataBits: 8,
        parity: "none",
        stopBits: 1
    };
    bufferedData = [];
    pendingReads = [];
    telegramEndTimeoutMs;
    writeLog = [];
    constructor(options = {}) {
        this.telegramEndTimeoutMs =
            options.telegramEndTimeoutMs ?? DEFAULT_TELEGRAM_END_TIMEOUT_MS;
    }
    async open(port) {
        void port;
        this.isOpen = true;
    }
    async close() {
        this.isOpen = false;
        this.bufferedData = [];
        this.failPendingReads(new Error("Serial port closed"));
    }
    async configure(config) {
        this.config = { ...config };
    }
    async setDtr(value) {
        void value;
        this.assertOpen();
    }
    async setRts(value) {
        void value;
        this.assertOpen();
    }
    async setBreak(durationMs) {
        void durationMs;
        this.assertOpen();
    }
    setTelegramEndTimeout(ms) {
        if (Number.isFinite(ms) && ms > 0) {
            this.telegramEndTimeoutMs = ms;
        }
    }
    async purge() {
        this.bufferedData = [];
    }
    async read(length, timeoutMs) {
        if (length <= 0) {
            return new Uint8Array();
        }
        this.assertOpen();
        const fromBuffer = this.consumeBuffered(length);
        if (fromBuffer.length >= length) {
            return fromBuffer;
        }
        return new Promise((resolve, reject) => {
            const pending = {
                length,
                collected: Array.from(fromBuffer),
                resolve,
                reject
            };
            if (timeoutMs > 0) {
                pending.timeoutId = setTimeout(() => {
                    this.removePending(pending);
                    reject(new errors_1.SerialTimeoutError());
                }, timeoutMs);
            }
            if (pending.collected.length > 0) {
                this.resetTelegramTimer(pending);
            }
            this.pendingReads.push(pending);
        });
    }
    async write(data) {
        this.assertOpen();
        this.writeLog.push(Uint8Array.from(data));
    }
    enqueueRead(data, delayMs = 0) {
        const payload = data instanceof Uint8Array ? data : Uint8Array.from(data);
        const chunk = { data: payload, delayMs };
        if (delayMs > 0) {
            void this.scheduleChunkAsync(chunk);
        }
        else {
            this.scheduleChunkSync(chunk);
        }
    }
    getWrites() {
        return [...this.writeLog];
    }
    async scheduleChunkAsync(chunk) {
        await new Promise((resolve) => setTimeout(resolve, chunk.delayMs));
        this.scheduleChunkSync(chunk);
    }
    scheduleChunkSync(chunk) {
        for (const value of chunk.data.values()) {
            this.bufferedData.push(value);
        }
        this.flushPendingReads();
    }
    consumeBuffered(length) {
        if (this.bufferedData.length === 0 || length <= 0) {
            return new Uint8Array();
        }
        const takeCount = Math.min(length, this.bufferedData.length);
        const chunk = this.bufferedData.splice(0, takeCount);
        return Uint8Array.from(chunk);
    }
    flushPendingReads() {
        while (this.pendingReads.length > 0) {
            const pending = this.pendingReads[0];
            const available = this.consumeBuffered(pending.length - pending.collected.length);
            if (available.length > 0) {
                pending.collected.push(...available);
                this.resetTelegramTimer(pending);
            }
            if (pending.collected.length >= pending.length) {
                this.pendingReads.shift();
                this.resolvePending(pending);
                continue;
            }
            if (pending.collected.length > 0) {
                this.resetTelegramTimer(pending);
            }
            break;
        }
    }
    resetTelegramTimer(pending) {
        if (this.telegramEndTimeoutMs <= 0) {
            return;
        }
        if (pending.telegramTimeoutId) {
            clearTimeout(pending.telegramTimeoutId);
        }
        pending.telegramTimeoutId = setTimeout(() => {
            this.removePending(pending);
            pending.resolve(Uint8Array.from(pending.collected));
        }, this.telegramEndTimeoutMs);
    }
    resolvePending(pending) {
        if (pending.timeoutId) {
            clearTimeout(pending.timeoutId);
        }
        if (pending.telegramTimeoutId) {
            clearTimeout(pending.telegramTimeoutId);
        }
        pending.resolve(Uint8Array.from(pending.collected));
    }
    failPendingReads(error) {
        const pending = this.pendingReads.splice(0);
        for (const entry of pending) {
            if (entry.timeoutId) {
                clearTimeout(entry.timeoutId);
            }
            if (entry.telegramTimeoutId) {
                clearTimeout(entry.telegramTimeoutId);
            }
            entry.reject(error);
        }
    }
    removePending(pending) {
        const index = this.pendingReads.indexOf(pending);
        if (index >= 0) {
            this.pendingReads.splice(index, 1);
        }
        if (pending.timeoutId) {
            clearTimeout(pending.timeoutId);
        }
        if (pending.telegramTimeoutId) {
            clearTimeout(pending.telegramTimeoutId);
        }
    }
    assertOpen() {
        if (!this.isOpen) {
            throw new Error("Serial port not open");
        }
    }
}
exports.MockSerialTransport = MockSerialTransport;
//# sourceMappingURL=mockSerialTransport.js.map